import React, {Dispatch, SetStateAction, useCallback, useEffect, useState} from 'react';
import {
  OperacionTransferenciaTercerosCuentaDetalleProvisional,
  schemaOperacionTransferenciaTercerosCuentaDetalleProvisional
} from "../types/dto";
import {useConfirmationDialog} from "../../../../../hooks/useConfirmationDialog";
import {useForm, useWatch} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod/dist/zod";
import {v4 as uuid} from "uuid";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField as MUITextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {ICON_MAS} from "../../../components/SharedIcons";
import CardActions from "../../../components/CardActions";
import {FieldView} from '../../../components/FieldView';
import DeleteIcon from "@mui/icons-material/Delete";
import {OperacionTransferenciaTercerosCuentaFields, OperacionTransferenciaTercerosDetalleFields} from "../types/fields";
import {HttpCacheOperacion} from "../../../api/httpCacheOperacion";
import {AsyncSelect, ControlledTextField} from "../../../../../components/forms";
import {TextField} from "src/lib/itaptec/components_forms/src";
import {EntityWithIdAndDescription, EntityWithIdAndDescriptionFields} from 'src/types';
import {OperacionTransferenciaTercerosTipoOperacion} from '../types/enums';
import {FilePutFields} from "../../../api/file-fields";
import {ArchivoFormData} from "../../../components/DialogNuevoArchivo";
import {DropzoneField} from "../../../../../components/forms/DropzoneField";

interface SeccionTransferenciaTercerosCuentaNuevoProps {
  codPais: number,
  cuentasProvisionales?: OperacionTransferenciaTercerosCuentaDetalleProvisional[],
  setCuentasProvisionales: Dispatch<SetStateAction<OperacionTransferenciaTercerosCuentaDetalleProvisional[]>>;
}

function SeccionTransferenciaTercerosCuentaNuevo({
                                                   codPais,
                                                   cuentasProvisionales,
                                                   setCuentasProvisionales
                                                 }: SeccionTransferenciaTercerosCuentaNuevoProps) {
  const {askConfirmation} = useConfirmationDialog()

  const [openDialog, setOpenDialog] = useState<boolean>(false)
  const [freeSoloMoneda, setFreeSoloMoneda] = useState<boolean>(false)
  const [tiposOperacion, setTiposOperacion] = useState<EntityWithIdAndDescription[]>([])

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    resetField
  } = useForm<OperacionTransferenciaTercerosCuentaDetalleProvisional>({
    resolver: zodResolver(schemaOperacionTransferenciaTercerosCuentaDetalleProvisional),
    defaultValues: {
      idProvisional: uuid(),
      [OperacionTransferenciaTercerosDetalleFields.Moneda]: 'Dólares',
    }
  })

  const watchTipoOperacion = useWatch({
    control: control,
    name: OperacionTransferenciaTercerosCuentaFields.CodTipoOperacion
  })

  const watchMoneda = useWatch({
    control: control,
    name: OperacionTransferenciaTercerosDetalleFields.Moneda
  })

  const watchDocumentacion = useWatch({
    control: control,
    name: FilePutFields.File
  })

  useEffect(() => {
    if (watchMoneda === null) {
      setFreeSoloMoneda(true)
    }
  }, [watchMoneda])

  const alGuardarCuentaProvisional = (data: OperacionTransferenciaTercerosCuentaDetalleProvisional) => {
    const nuevaCuenta: OperacionTransferenciaTercerosCuentaDetalleProvisional = {
      ...data,
      idProvisional: uuid()
    }

    setCuentasProvisionales(prev => [...prev, nuevaCuenta])
    reset()
    setOpenDialog(false)
  }

  const getTipoOperacion = useCallback(() => {

    return HttpCacheOperacion.getTransferenciaTercerosTipos(codPais).then((data) => {
      setTiposOperacion(data)

      return data
    })
  }, [codPais])
  const getMonedas = useCallback(() => HttpCacheOperacion.getTransferenciaTercerosMonedas(watchTipoOperacion), [watchTipoOperacion])

  useEffect(() => {
    const descTipoOperacion = tiposOperacion.find(tipo => tipo.id === watchTipoOperacion)?.descripcion
    setValue(OperacionTransferenciaTercerosCuentaFields.TipoOperacion, descTipoOperacion)
  }, [watchTipoOperacion])

  return (
    <CardActions
      title={'Cuentas'}
      icon='mdi:bank-transfer'
      actions={
        <Button variant='contained' color='primary' endIcon={ICON_MAS} onClick={() => setOpenDialog(true)}>
          Nuevo
        </Button>
      }
      colapsable
    >

      {!cuentasProvisionales?.length ? (
        <Alert severity='info'>No se encontraron detalles para esta operación.</Alert>
      ) : (
        <Stack spacing={4}>
          {cuentasProvisionales.map(cuenta => (
            <Card key={cuenta.idProvisional}>
              <Box display='flex' justifyContent='flex-end'>
                <Tooltip title='Eliminar'>
                  <IconButton
                    onClick={() =>
                      askConfirmation(() =>
                        setCuentasProvisionales(dts =>
                          dts.filter(
                            dt =>
                              dt.idProvisional !==
                              cuenta.idProvisional
                          )
                        )
                      )
                    }
                  >
                    <DeleteIcon color='error'/>
                  </IconButton>
                </Tooltip>
              </Box>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FieldView label='Número de cuenta' value={cuenta[OperacionTransferenciaTercerosCuentaFields.NroCuenta]} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FieldView label='Titular' value={cuenta[OperacionTransferenciaTercerosCuentaFields.Titular]} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FieldView label='Tipo Operación' value={cuenta[OperacionTransferenciaTercerosCuentaFields.TipoOperacion]} />
                  </Grid>
                  {cuenta?.[OperacionTransferenciaTercerosCuentaFields.Broker] && (
                    <Grid item xs={12} md={4}>
                      <FieldView label='Broker' value={cuenta[OperacionTransferenciaTercerosCuentaFields.Broker]} />
                    </Grid>
                  )}
                  {cuenta?.[OperacionTransferenciaTercerosCuentaFields.MSB] && (
                    <Grid item xs={12} md={4}>
                      <FieldView label='MSB' value={cuenta[OperacionTransferenciaTercerosCuentaFields.MSB]} />
                    </Grid>
                  )}
                  {cuenta?.[OperacionTransferenciaTercerosCuentaFields.BancoReceptor] && (
                    <Grid item xs={12} md={4}>
                      <FieldView label='Banco Receptor' value={cuenta[OperacionTransferenciaTercerosCuentaFields.BancoReceptor]} />
                    </Grid>
                  )}
                  {cuenta?.[OperacionTransferenciaTercerosCuentaFields.TitularCuenta] && (
                    <Grid item xs={12} md={4}>
                      <FieldView label='Titular Cuenta' value={cuenta[OperacionTransferenciaTercerosCuentaFields.TitularCuenta]} />
                    </Grid>
                  )}
                  {cuenta?.[OperacionTransferenciaTercerosDetalleFields.Moneda] && (
                    <Grid item xs={12} md={4}>
                      <FieldView label='Moneda' value={cuenta[OperacionTransferenciaTercerosDetalleFields.Moneda]} />
                    </Grid>
                  )}
                  {cuenta?.[OperacionTransferenciaTercerosDetalleFields.Monto] && (
                    <Grid item xs={12} md={4}>
                      <FieldView label='Monto' value={cuenta[OperacionTransferenciaTercerosDetalleFields.Monto]} />
                    </Grid>
                  )}
                  {cuenta?.[OperacionTransferenciaTercerosDetalleFields.MontoEquivalenteUSD] && (
                    <Grid item xs={12} md={4}>
                      <FieldView label='Monto Equivalente USD' value={cuenta[OperacionTransferenciaTercerosDetalleFields.MontoEquivalenteUSD]} />
                    </Grid>
                  )}
                  {cuenta?.[OperacionTransferenciaTercerosDetalleFields.Destino] && (
                    <Grid item xs={12} md={4}>
                      <FieldView label='Destino' value={cuenta[OperacionTransferenciaTercerosDetalleFields.Destino]} />
                    </Grid>
                  )}
                </Grid>
              </CardContent>

            </Card>
          ))}
        </Stack>
      )}

      <Dialog open={openDialog} maxWidth='lg' fullWidth>
        <DialogTitle>Nueva cuenta</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{mt: 1}}>

            <Grid item xs={6} md={4}>
              <TextField
                control={control}
                name={OperacionTransferenciaTercerosCuentaFields.NroCuenta}
                label='Número de cuenta'
                mask={{
                  mask: Number
                }}
                size={'small'}
                fullWidth
              />
            </Grid>

            <Grid item xs={6} md={4}>
              <ControlledTextField
                control={control}
                name={OperacionTransferenciaTercerosCuentaFields.Titular}
                label='Titular'
                fullWidth
              />
            </Grid>

            <Grid item container xs={12} spacing={1}>
              <Grid item xs={6} md={4}>
                <AsyncSelect
                  control={control}
                  name={OperacionTransferenciaTercerosCuentaFields.CodTipoOperacion}
                  label='Tipo Operación'
                  loadOptions={getTipoOperacion}
                  selectNotNulleable
                  fullWidth
                />
              </Grid>


              {
                watchTipoOperacion === OperacionTransferenciaTercerosTipoOperacion.FCC
                  ?
                  <>
                    <Grid item xs={6} md={4}>
                      <ControlledTextField
                        control={control}
                        name={OperacionTransferenciaTercerosCuentaFields.Broker}
                        label='Broker'
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <ControlledTextField
                        control={control}
                        name={OperacionTransferenciaTercerosCuentaFields.MSB}
                        label='MSB'
                        fullWidth
                      />
                    </Grid>
                  </>
                  : watchTipoOperacion === OperacionTransferenciaTercerosTipoOperacion.TRANSFERENCIATERCEROS
                    ?
                    <>
                      <Grid item xs={6} md={4}>
                        <ControlledTextField
                          control={control}
                          name={OperacionTransferenciaTercerosCuentaFields.BancoReceptor}
                          label='Banco Receptor'
                          fullWidth
                        />
                      </Grid>
                      <Grid item xs={6} md={4}>
                        <ControlledTextField
                          control={control}
                          name={OperacionTransferenciaTercerosCuentaFields.TitularCuenta}
                          label='Titular Cuenta'
                          fullWidth
                        />
                      </Grid>
                    </>
                    :
                    <>
                      <Grid item xs={6} md={4}>
                        <MUITextField disabled label={'Broker/Banco'} size={'small'} fullWidth/>

                      </Grid>
                      <Grid item xs={6} md={4}>
                        <MUITextField disabled label={'MSB/Titular'} size={'small'} fullWidth/>

                      </Grid>
                    </>
              }

            </Grid>


            <Grid item xs={6} md={3}>
              {
                !freeSoloMoneda
                  ? <AsyncSelect
                    control={control}
                    name={OperacionTransferenciaTercerosDetalleFields.Moneda}
                    label='Moneda'
                    fullWidth
                    nullOptionLabel={'Otro'}
                    loadOptions={getMonedas}
                    valueField={EntityWithIdAndDescriptionFields.Description}
                    disabled={!watchTipoOperacion}

                  />
                  : <Stack direction={'row'}>
                    <TextField
                      name={OperacionTransferenciaTercerosDetalleFields.Moneda}
                      label='Moneda'
                      control={control}
                      size='small'
                      autoFocus
                      fullWidth
                    />
                    <Button onClick={() => {
                      setFreeSoloMoneda(false)
                    }}>Opciones</Button>
                  </Stack>
              }
            </Grid>

            <Grid item xs={6} md={3}>
              <ControlledTextField
                control={control}
                name={OperacionTransferenciaTercerosDetalleFields.Monto}
                label='Monto'
                currency
                size={'small'}
                fullWidth
              />
            </Grid>


            {
              watchMoneda !== 'Dólares'
              &&
                <Grid item xs={6} md={3}>
                    <ControlledTextField
                        control={control}
                        name={OperacionTransferenciaTercerosDetalleFields.MontoEquivalenteUSD}
                        label='Monto Equivalente USD'
                        size={'small'}
                        currency
                        fullWidth
                    />
                </Grid>
            }
            <Grid item xs={6} md={3}>
              <ControlledTextField control={control} name={OperacionTransferenciaTercerosDetalleFields.Destino}
                                   label='Destino' fullWidth

              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant='body2'>Documentación Respaldatoria</Typography>
              {
                watchDocumentacion ?

                  <ArchivoFormData
                    archivo={watchDocumentacion}
                    onRemove={() => resetField(FilePutFields.File)}
                  />
                  :
                  <DropzoneField
                    name={FilePutFields.File}
                    control={control}
                    setValue={setValue}
                  />
              }
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant='text' color='primary' onClick={() => setOpenDialog(false)}>
            Cancelar
          </Button>
          <Button variant='contained' color='primary' onClick={handleSubmit(alGuardarCuentaProvisional)}>
            Agregar
          </Button>
        </DialogActions>
      </Dialog>

    </CardActions>
  );
}

export default SeccionTransferenciaTercerosCuentaNuevo;